import { useQuery } from "@tanstack/react-query";
import { type Dayjs } from "dayjs";

import { CalendarEventType } from "@fitness-app/data-models/entities/CalendarEvent";
import { fetchAllPostgrestRows } from "@fitness-app/utils/src/helpers/fetchAllPostgrestRows";

import { supabase } from "~/store/initializeStore";
import { TRAINER_CONSULTATION_COLUMNS, type TrainerConsultation } from "./consultationModel";

/**
 * Every consultation one trainer hosts in the range, i.e. all of their clients at once. Filtered by
 * `userId` - the host's auth id the Calendly listener resolves from the booking's host e-mail - which
 * is both indexed and what RLS matches for a trainer reading their own rows ("Enable all actions by
 * assigned trainer"). Reading someone else's rows takes `clients.read` ALL or the owner role.
 */
export const useTrainerConsultations = (userId: string | null | undefined, range: { start: Dayjs; end: Dayjs }) => {
  const from = range.start.toISOString();
  const to = range.end.toISOString();

  return useQuery(["trainer_consultations", userId, from, to], {
    enabled: Boolean(userId),
    keepPreviousData: true,
    refetchOnWindowFocus: true,
    // A busy month (~30 calls a day, cancelled included) can pass PostgREST's 1000-row cap, which
    // truncates silently. `id` after `startTime` makes the paging order unique.
    queryFn: () =>
      fetchAllPostgrestRows<TrainerConsultation>(({ from: rangeFrom, to: rangeTo, withCount }) =>
        supabase
          .from("calendar_event")
          .select(TRAINER_CONSULTATION_COLUMNS, withCount ? { count: "exact" } : undefined)
          .eq("userId", userId!)
          .eq("type", CalendarEventType.ONLINE_CONSULTATIONS)
          .gte("startTime", from)
          .lte("startTime", to)
          .order("startTime", { ascending: true })
          .order("id", { ascending: true })
          .range(rangeFrom, rangeTo)
          .returns<TrainerConsultation[]>(),
      ),
  });
};
